import {ContentState} from "./content_module";
import {ActionContext} from "vuex";
import {Content} from "../../../../presentation/views/content/helpers";

type ContentControlActionContext = ActionContext<ContentControlState, ContentState>

class ContentControlState {
  navigableContents: Content[] = [];
  previousIndex: number = -1;
  currentIndex: number = -1;
  nextIndex: number = -1;
  lastIndex: number = -1;
  lockedIndex: number = -1;
}

const state = new ContentControlState();

const mutations = {
  SET_NAVIGABLE_CONTENTS(state: ContentControlState, contents: Content[]) {
    state.navigableContents = contents;
  },
  SET_PREVIOUS_INDEX(state: ContentControlState, index: number) {
    state.previousIndex = index;
  },
  SET_CURRENT_INDEX(state: ContentControlState, index: number) {
    state.currentIndex = index;
  },
  SET_NEXT_INDEX(state: ContentControlState, index: number) {
    state.nextIndex = index;
  },
  SET_LAST_INDEX(state: ContentControlState, index: number) {
    state.lastIndex = index;
  },
  SET_LOCKED_INDEX(state: ContentControlState, index: number) {
    state.lockedIndex = index;
  },
};

/** Return only contents available to read from the content_provider list. */
const extractNavigableContents = (contents: Content[]): Content[] => contents.filter((content) => {
  return content.isReleased && !content.isBlocked;
});

const actions = {
  reload(ctx: ContentControlActionContext) {
    const content = ctx.rootGetters["content_module/currentContent"];
    const rawContents = ctx.rootGetters["content_module/contents"];
    const contents = extractNavigableContents(rawContents);

    const lastIndex = contents.length - 1;

    const currentIndex = contents.findIndex((it) => it.id === content.id);
    let lockIndex = -1;

    rawContents.forEach((it, index) => {
      /** set index of the locked content. */
      if (it.isBlocked && lockIndex === -1) {
        lockIndex = index;
      }
    });

    const nextIndex = currentIndex + 1 > lastIndex ? lastIndex : currentIndex + 1;
    const previousIndex = currentIndex - 1 < 0 ? 0 : currentIndex - 1;

    ctx.commit("SET_NAVIGABLE_CONTENTS", contents);
    ctx.commit("SET_PREVIOUS_INDEX", previousIndex);
    ctx.commit("SET_CURRENT_INDEX", currentIndex);
    ctx.commit("SET_NEXT_INDEX", nextIndex);
    ctx.commit("SET_LAST_INDEX", lastIndex);
    ctx.commit("SET_LOCKED_INDEX", lockIndex);
  },

  async loadPrevious(ctx: ContentControlActionContext) {
    try {
      const previousContent = ctx.state.navigableContents[ctx.state.previousIndex];
      await ctx.dispatch("content_module/setCurrentContent", previousContent.id, {root: true});
    } catch (e) {
      console.error("[loadPrevious] Error setCurrentContent ", e);
      throw e;
    }
  },

  async loadNext(ctx: ContentControlActionContext) {
    try {
      const nextContent = ctx.state.navigableContents[ctx.state.nextIndex];
      await ctx.dispatch("content_module/setCurrentContent", nextContent.id, {root: true});
    } catch (e) {
      console.error("[loadNext] Error setCurrentContent ", e);
      throw e;
    }
  }
};

const getters = {
  hasNext: (state: ContentControlState) => state.currentIndex < state.lastIndex,
  isNextLocked: (state: ContentControlState) => {
    return state.currentIndex + 1 === state.lockedIndex
  },
  hasPrevious: (state: ContentControlState) => state.currentIndex > 0,
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}